import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Phone,
  Mail,
  Globe,
  MapPin,
  Clock,
  ShieldCheck,
  ArrowRight,
  Check,
  FileSignature,
  Home as HomeIcon,
  Car,
  Stamp,
  ScrollText,
  KeyRound,
  Sparkles,
  Star,
  Quote,
  Printer,
  Plus,
  Minus,
  ClipboardCheck,
  UserCheck,
  CalendarCheck,
  Landmark,
  Building2,
  Award,
  BadgeCheck,
} from "lucide-react";

import { NAV, HERO, SERVICES, ABOUT, TESTIMONIALS, WHY, HOW, AREAS, FAQ, CONTACT, FOOTER } from "@/constants/testIds";
import HiddenLeadForm, { relayToLeadForms } from "@/components/HiddenLeadForm";
import { PrivacyPolicy, Terms } from "@/pages/Legal";
import useLenis from "@/hooks/useLenis";
import { Reveal, StaggerGroup, StaggerItem, Parallax, FlyIn, motion } from "@/components/Motion";
import AfterHoursHotline from "@/components/AfterHoursHotline";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZCAL_URL = "https://knight-notary-services.ninjapipe.app/book/knight-notary-services";
const CLIENT_PORTAL_URL = "#"; // TODO: replace with real client portal URL

const LOGO_URL = "/logo-transparent.png";
const SHIELD_ONLY_URL = "/shield-transparent.png";
const HERO_BG_URL =
  "https://customer-assets.emergentagent.com/job_knight-docs/artifacts/8shigut2_KNS-DLK-stamp1.png";

// Social profiles — placeholders; update with real URLs when available.
const SOCIALS = [
  { name: "LinkedIn", href: "https://www.linkedin.com/", testid: "social-linkedin" },
  { name: "Instagram", href: "https://www.instagram.com/", testid: "social-instagram" },
  { name: "Facebook", href: "https://www.facebook.com/", testid: "social-facebook" },
  { name: "X", href: "https://x.com/", testid: "social-x" },
  { name: "TikTok", href: "https://www.tiktok.com/", testid: "social-tiktok" },
  { name: "YouTube", href: "https://www.youtube.com/", testid: "social-youtube" },
];

// Inline brand SVG icons (16x16, currentColor)
const SocialIcon = ({ name, className = "w-4 h-4" }) => {
  switch (name) {
    case "Instagram":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
        </svg>
      );
    case "Threads":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M12.05 21.5c-3.06 0-5.41-1.02-6.99-3.03C3.62 16.66 2.85 14.13 2.83 12v-.04c.02-2.13.79-4.66 2.23-6.47C6.64 3.5 8.99 2.5 12.05 2.5c2.69 0 4.93.84 6.54 2.42 1.18 1.16 1.95 2.69 2.29 4.55l-1.83.5c-.59-3.2-2.43-4.86-5.56-5.07-2.23.05-3.93.78-5.07 2.18-.97 1.21-1.55 2.93-1.66 4.92.11 1.99.69 3.71 1.66 4.92 1.14 1.4 2.84 2.13 5.07 2.18 2.07-.05 3.45-.55 4.42-1.5.61-.59.97-1.36 1.07-2.29-.13-.45-.39-.83-.76-1.11-.43-.31-.97-.5-1.58-.55-.27 1.45-.84 2.51-1.71 3.17-.78.59-1.78.88-2.97.88-1.06 0-1.96-.27-2.66-.81-.78-.59-1.21-1.43-1.21-2.39 0-.97.46-1.81 1.34-2.42.86-.59 2-.91 3.4-.91.69 0 1.34.06 1.96.18-.04-.86-.37-1.45-.97-1.81-.5-.3-1.21-.43-2.07-.43-.91 0-1.71.36-2.34.99l-1.27-1.27c.93-.93 2.19-1.43 3.61-1.43 1.18 0 2.21.27 2.97.81.79.56 1.27 1.41 1.4 2.55 1.05.18 1.93.55 2.6 1.11.96.79 1.5 1.99 1.5 3.34 0 1.66-.62 3.04-1.79 4-1.32 1.09-3.16 1.65-5.45 1.69h-.03zm-.4-7.32c-.97 0-1.79.19-2.36.55-.45.29-.69.65-.69 1.05 0 .35.17.62.5.86.37.27.91.41 1.55.41.93 0 1.66-.2 2.17-.59.46-.35.74-.91.84-1.66l.04-.31a8.9 8.9 0 0 0-2.05-.31z"/>
        </svg>
      );
    case "Facebook":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M13.5 21v-7.5h2.5l.4-3h-2.9V8.6c0-.86.24-1.45 1.49-1.45H16.5V4.46A21.7 21.7 0 0 0 14.34 4.3c-2.14 0-3.6 1.3-3.6 3.7v2.5H8.2v3h2.54V21h2.76z"/>
        </svg>
      );
    case "X":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M18.244 3H21l-6.52 7.45L22 21h-6.828l-4.78-6.246L4.8 21H2l7.005-8L2 3h6.914l4.32 5.71L18.244 3zm-2.39 16h1.59L8.23 5h-1.7l9.323 14z"/>
        </svg>
      );
    case "TikTok":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M16.5 3h-2.7v12.05a2.55 2.55 0 1 1-2.55-2.55c.2 0 .39.02.58.06v-2.74a5.3 5.3 0 1 0 4.67 5.23V8.1a6.43 6.43 0 0 0 3.85 1.27V6.63a3.83 3.83 0 0 1-3.85-3.63z"/>
        </svg>
      );
    case "YouTube":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.27 5 12 5 12 5s-6.27 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26.1 26.1 0 0 0 2 12a26.1 26.1 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.73 19 12 19 12 19s6.27 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26.1 26.1 0 0 0 22 12a26.1 26.1 0 0 0-.4-4.8zM10 15V9l5.2 3L10 15z"/>
        </svg>
      );
    case "LinkedIn":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor">
          <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.44-2.14 2.94v5.67H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.36-1.85c3.59 0 4.25 2.37 4.25 5.45v6.29zM5.34 7.44a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/>
        </svg>
      );
    default:
      return null;
  }
};
const CINCY_URL =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1e/Paycor_Stadium_2025.jpg/1920px-Paycor_Stadium_2025.jpg";
const CINCY_AERIAL_URL =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1e/Paycor_Stadium_2025.jpg/1920px-Paycor_Stadium_2025.jpg";
const PEN_URL =
  "https://static.prod-images.emergentagent.com/jobs/7f968b21-7a03-4711-989c-287f65dfc099/images/37f683b69b19edd0960d6a9f3c1243bf5d8b7ca2b17b4aedab0d15049a5a6283.png";
const SIGNING_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_knight-docs/artifacts/fwk32xs8_image%20%288%29.webp";

/* Themed imagery per service — Unsplash */
const SVC_IMG = {
  loanSigning:
    "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  realEstate:
    "https://images.unsplash.com/photo-1568605114967-8130f3a36994?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  mobile:
    "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  apostille:
    "https://images.unsplash.com/photo-1518623489648-a173ef7824f3?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  generalNotary:
    "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  poa:
    "https://images.unsplash.com/photo-1521790797524-b2497295b8a0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
};

const SERVICES_LIST = [
  {
    slug: "loan-signing",
    title: "Loan Signing",
    blurb:
      "Certified signing agent for mortgages, refinances, and HELOCs. Handled with precision and discretion.",
    icon: FileSignature,
    image: SVC_IMG.loanSigning,
    tag: "Certified Signing Agent",
  },
  {
    slug: "real-estate",
    title: "Real Estate",
    blurb:
      "Closing documents, deeds, and seller packages — notarized on your schedule.",
    icon: HomeIcon,
    image: SVC_IMG.realEstate,
    tag: "Closings · Deeds",
  },
  {
    slug: "mobile-notary",
    title: "Mobile Notary",
    blurb:
      "We come to your home, office, hospital, or coffee shop across Greater Cincinnati.",
    icon: Car,
    image: SVC_IMG.mobile,
    tag: "We Come To You",
  },
  {
    slug: "apostille",
    title: "Apostille",
    blurb:
      "International document authentication and Secretary of State liaison.",
    icon: Stamp,
    image: SVC_IMG.apostille,
    tag: "International",
  },
  {
    slug: "general-notary",
    title: "General Notary",
    blurb:
      "Affidavits, acknowledgments, jurats, copy certifications — done right the first time.",
    icon: ScrollText,
    image: SVC_IMG.generalNotary,
    tag: "Everyday Work",
  },
  {
    slug: "power-of-attorney",
    title: "Power of Attorney",
    blurb:
      "Durable, medical, and financial POA notarization with confidential handling.",
    icon: KeyRound,
    image: SVC_IMG.poa,
    tag: "Confidential",
  },
];

/* Reusable SVG gradient defs for metallic gold icon strokes.
   Included once at App root so `stroke="url(#kn-gold-grad)"` works everywhere. */
const GoldIconDefs = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
    <defs>
      <linearGradient id="kn-gold-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8C6A1A" />
        <stop offset="25%" stopColor="#D4AF37" />
        <stop offset="50%" stopColor="#F8E9A1" />
        <stop offset="55%" stopColor="#FFFBE6" />
        <stop offset="75%" stopColor="#F0D87C" />
        <stop offset="100%" stopColor="#8C6A1A" />
      </linearGradient>
      <linearGradient id="kn-gold-grad-v" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F8E9A1" />
        <stop offset="45%" stopColor="#D4AF37" />
        <stop offset="100%" stopColor="#8C6A1A" />
      </linearGradient>
    </defs>
  </svg>
);

/* -------------------- NAV -------------------- */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={NAV.root}
      className="fixed top-0 left-0 right-0 z-50"
    >
      {/* Top utility strip */}
      <div
        data-testid="topbar-root"
        className="bg-[#0B1437] text-white text-[15.5px] font-body"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-11 grid grid-cols-3 items-center gap-4">
          <div className="flex items-center gap-5 min-w-0 justify-self-start">
            <a
              href="tel:+15132966926"
              data-testid="topbar-phone"
              className="flex items-center gap-2 text-white hover:text-[#F8E9A1] transition-colors whitespace-nowrap"
            >
              <Phone className="w-5 h-5" style={{ stroke: "url(#kn-gold-grad)" }} />
              <span>1 (513) 296-6926</span>
            </a>
            <span className="hidden sm:inline w-px h-4 bg-white/30" />
            <span
              data-testid="topbar-fax"
              className="hidden sm:flex items-center gap-2 text-white whitespace-nowrap"
            >
              <Printer className="w-5 h-5" style={{ stroke: "url(#kn-gold-grad)" }} />
              <span>Fax 1 (513) 399-4989</span>
            </span>
          </div>

          <span
            data-testid="topbar-247"
            className="hidden md:inline-flex justify-self-center items-center gap-2 kn-metallic-gold-text font-semibold tracking-wider"
          >
            <span className="w-2 h-2 rounded-full kn-pulse-dot" />
            Available 24/7
          </span>

          <div className="flex items-center gap-2 justify-self-end">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.name}
                data-testid={s.testid}
                className="kn-header-social group w-9 h-9 flex items-center justify-center rounded-full bg-white/10 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/15 hover:border-[#F8E9A1] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_4px_18px_rgba(212,175,55,0.25)]"
              >
                <SocialIcon name={s.name} className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`transition-all duration-300 ${
          scrolled ? "kn-glass" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-3 items-center gap-6 py-2">
          <a
            href="#top"
            data-testid={NAV.logo}
            className="flex items-center group justify-self-start"
          >
            <img
              src={LOGO_URL}
              alt="Knight Notary Services"
              className="h-[81px] lg:h-[94px] w-auto object-contain"
            />
          </a>

          <nav className="hidden md:flex justify-self-center items-center gap-9 text-[15px] text-[#1E2A5E] font-body font-medium">
            <a
              href="#services"
              data-testid={NAV.linkServices}
              className="kn-nav-link hover:text-[#0B1437] transition-colors"
            >
              Services
            </a>
            <a
              href="#about"
              data-testid={NAV.linkAbout}
              className="kn-nav-link hover:text-[#0B1437] transition-colors"
            >
              About
            </a>
            <a
              href="#contact"
              data-testid={NAV.linkContact}
              className="kn-nav-link hover:text-[#0B1437] transition-colors"
            >
              Contact
            </a>
          </nav>

          <div className="flex items-center gap-2 justify-self-end">
            <a
              href={ZCAL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-cta-book"
              className="text-[13px] py-2.5 px-5 rounded-full inline-flex items-center gap-2 font-semibold text-[#0B1437] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-14px_rgba(212,175,55,0.7)]"
              style={{
                background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 45%,#B8902F 100%)",
                boxShadow: "0 10px 26px -10px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.7)",
              }}
            >
              Book a notary
            </a>
            <a
              href={CLIENT_PORTAL_URL}
              data-testid="nav-cta-client-portal"
              className="text-[13px] py-2.5 px-5 rounded-full inline-flex items-center gap-2 font-semibold border border-[#D4AF37]/60 hover:border-[#F8E9A1] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-8px_rgba(212,175,55,0.55)] transition-all duration-300"
              style={{ background: "#0B1437" }}
            >
              <span className="kn-metallic-gold-text">Client Portal</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

/* -------------------- HERO -------------------- */
const Hero = () => {
  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative pt-40 lg:pt-44 pb-28 lg:pb-36 min-h-[92vh] lg:min-h-[96vh] overflow-hidden bg-[#0B1437] kn-grain"
    >
      {/* Parallax hero image */}
      <motion.img
        src={HERO_BG_URL}
        alt="Damon Knight, certified notary, stamping a promissory note"
        data-testid={HERO.logoImg}
        className="absolute inset-0 w-full h-[115%] -top-[7%] object-cover object-center"
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: [0.2, 0.7, 0.15, 1] }}
        style={{ willChange: "transform" }}
      />

      {/* Ambient gold and navy glow blobs */}
      <div className="kn-ambient-glow" style={{ background: "#D4AF37", width: 520, height: 520, top: "-8%", right: "-6%" }} />
      <div className="kn-ambient-glow" style={{ background: "#1E2A5E", width: 620, height: 620, bottom: "-14%", left: "-10%", opacity: 0.75 }} />

      {/* Legibility overlays */}
      <div className="absolute inset-0 bg-gradient-to-l from-[#0B1437]/85 via-[#0B1437]/55 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1437]/70 via-transparent to-[#0B1437]/40" />
      <div className="absolute inset-0 kn-noise opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-3xl">
          <motion.div
            data-testid={HERO.eyebrow}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05 }}
            className="inline-flex items-center gap-3 mb-8 uppercase text-[12px] tracking-[0.32em] font-medium"
          >
            <span className="w-6 h-px bg-[#D4AF37]" />
            <span className="kn-metallic-gold-text font-semibold">Knight Notary Services</span>
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/25 text-[10px] tracking-[0.22em] text-white/85 bg-white/5">
              <ShieldCheck className="w-3 h-3 text-[#F8E9A1]" />
              Insured & Bonded
            </span>
          </motion.div>

          <h1
            data-testid={HERO.title}
            className="font-display text-6xl sm:text-7xl lg:text-[7rem] font-light tracking-[-0.04em] text-white leading-[0.92] drop-shadow-[0_6px_28px_rgba(0,0,0,0.55)]"
          >
            <span className="kn-line-mask delay-1"><span>Your</span></span>
            <span className="kn-line-mask delay-2">
              <motion.span
                className="kn-metallic-gold-text italic font-extralight inline-block"
                animate={{
                  y: [0, -8, 0],
                  letterSpacing: ["-0.04em", "-0.02em", "-0.04em"],
                }}
                transition={{ duration: 5.5, ease: "easeInOut", repeat: Infinity, repeatType: "loop" }}
              >
                Signature
              </motion.span>
            </span>
            <span className="kn-line-mask delay-3">
              <motion.span
                className="kn-metallic-gold-text italic font-extralight inline-block"
                animate={{
                  y: [0, -6, 0],
                  letterSpacing: ["-0.04em", "-0.02em", "-0.04em"],
                }}
                transition={{ duration: 5.5, ease: "easeInOut", repeat: Infinity, repeatType: "loop", delay: 0.4 }}
              >
                Defender.
              </motion.span>
            </span>
          </h1>

          <motion.p
            data-testid={HERO.subtitle}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="mt-10 text-lg lg:text-xl text-slate-200 max-w-xl leading-relaxed font-body drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]"
          >
            Premium mobile notary services across Greater Cincinnati, Dayton, Northern KY, and Columbus. From loan signings to apostilles — handled with the precision and safety that your documents deserve.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-11 flex flex-wrap items-center gap-4"
          >
            <a
              href={ZCAL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid={HERO.ctaPrimary}
              className="group relative inline-flex items-center gap-3 rounded-full px-8 py-4 font-medium text-[#0B1437] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_28px_60px_-16px_rgba(212,175,55,0.75)]"
              style={{
                background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 45%,#B8902F 100%)",
                boxShadow: "0 18px 40px -14px rgba(212,175,55,0.6), inset 0 1px 0 rgba(255,255,255,0.7)",
              }}
            >
              <span className="tracking-wide">Book a notary</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#services"
              data-testid={HERO.ctaSecondary}
              className="kn-btn-ghost hover:-translate-y-0.5 hover:bg-white/10 transition-all duration-300"
              style={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.4)",
                boxShadow: "0 12px 32px -10px rgba(30,42,94,0.5)",
              }}
            >
              Explore services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm font-body"
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" style={{ stroke: "url(#kn-gold-grad)" }} />
              <span className="kn-metallic-gold-text font-semibold tracking-wide">Insured &amp; Bonded</span>
            </span>
            <motion.span
              className="flex items-center gap-2"
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            >
              <ShieldCheck className="w-4 h-4" style={{ stroke: "url(#kn-gold-grad)" }} />
              <span className="kn-metallic-gold-text font-semibold tracking-wide">Same-day appointments</span>
            </motion.span>
            <motion.span
              className="flex items-center gap-2"
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
            >
              <ShieldCheck className="w-4 h-4" style={{ stroke: "url(#kn-gold-grad)" }} />
              <span className="kn-metallic-gold-text font-semibold tracking-wide">Mobile appointments available</span>
            </motion.span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-12 flex flex-wrap gap-3"
          >
            <div className="inline-flex items-center gap-3 bg-white/8 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-3 shadow-[0_14px_40px_-15px_rgba(30,42,94,0.7)] hover:-translate-y-0.5 hover:shadow-[0_22px_55px_-12px_rgba(212,175,55,0.55)] hover:border-[#D4AF37]/70 hover:bg-white/12 transition-all duration-300 cursor-default">
              <MapPin className="w-4 h-4" style={{ stroke: "url(#kn-gold-grad)" }} />
              <div className="leading-tight">
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-300">Serving</p>
                <p className="font-display text-white text-base">Cincinnati · Dayton · Northern KY</p>
              </div>
            </div>
            <div className="inline-flex items-center gap-3 bg-white/8 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-3 shadow-[0_14px_40px_-15px_rgba(30,42,94,0.7)] hover:-translate-y-0.5 hover:shadow-[0_22px_55px_-12px_rgba(212,175,55,0.55)] hover:border-[#D4AF37]/70 hover:bg-white/12 transition-all duration-300 cursor-default">
              <Clock className="w-4 h-4" style={{ stroke: "url(#kn-gold-grad)" }} />
              <div className="leading-tight">
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-300">Open</p>
                <p className="font-display text-white text-base">After 7 PM Appointments Available</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 z-10"
      >
        <span className="text-[10px] tracking-[0.32em] uppercase text-white/60 font-medium">Scroll</span>
        <span className="relative w-px h-14 overflow-hidden">
          <span className="absolute inset-x-0 top-0 h-full bg-white/20" />
          <motion.span
            className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-[#F8E9A1] to-transparent"
            animate={{ y: [-24, 56] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
};

/* -------------------- TRUST STRIP (Metallic gold marquee) -------------------- */
const NNA_BADGE_URL =
  "https://customer-assets.emergentagent.com/job_knight-docs/artifacts/pbhpkhtj_recreate-in-4k_Own8-z-iXOaNnMg-6rr2ww_vVhfNGfCTK2xkmunwquRMg_cover_2k.png";

const TRUST_ITEMS = [
  { icon: ShieldCheck, label: "Insured & Bonded" },
  { icon: Award, label: "NNA Certified Signing Agent" },
  { icon: ShieldCheck, label: "Background Checked" },
  { icon: Star, label: "5-Star Rated" },
  { icon: Car, label: "Mobile Notary — 513 · Dayton · NKY · Columbus" },
  { icon: Clock, label: "Same-Day · Open Until 7 PM" },
  { icon: Check, label: "Insured & Bonded" },
  { icon: FileSignature, label: "Loan Signing Specialist" },
  { icon: Stamp, label: "Apostille Facilitation" },
];

const TrustStrip = () => {
  return (
    <section
      data-testid="trust-strip"
      className="kn-trust-gold relative overflow-hidden"
    >
      {/* Vertical band accents */}
      <div className="absolute inset-y-0 left-0 w-px bg-white/25" />
      <div className="absolute inset-y-0 right-0 w-px bg-white/25" />
      <div className="absolute inset-x-0 top-0 h-px bg-black/20" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-black/20" />

      <div className="relative py-6 lg:py-8">
        <div className="kn-fade-edge">
          <div className="kn-marquee-slow flex items-center gap-14 w-max whitespace-nowrap">
            {[...TRUST_ITEMS, ...TRUST_ITEMS].map((item, i) => {
              const Icon = item.icon;
              return (
                <span
                  key={i}
                  className="flex items-center gap-3 text-[#0B1437] font-display font-medium text-lg lg:text-xl tracking-tight"
                >
                  <Icon className="w-5 h-5" strokeWidth={2} />
                  <span>{item.label}</span>
                  <span className="ml-14 inline-block w-2 h-2 rounded-full bg-[#0B1437]/60" aria-hidden />
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- SERVICES -------------------- */
const Services = () => {
  return (
    <section
      id="services"
      data-testid={SERVICES.root}
      className="py-24 lg:py-32 bg-[#0B1437] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FlyIn from="left" className="max-w-2xl mb-16">
          <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>Services</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white">
            <span className="kn-metallic-gold-text">Every signature.</span>
            <br />
            <span className="kn-metallic-gold-text">Defended.</span>
          </h2>
          <p className="mt-6 text-slate-300 text-base lg:text-lg leading-relaxed font-body">
            A complete suite of notarial services performed by a commissioned
            agent who treats your documents like they matter — because they do.
            Every appointment is <span className="font-semibold text-white">insured &amp; bonded</span>.
          </p>
        </FlyIn>

        <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          {SERVICES_LIST.map((s, idx) => {
            const Icon = s.icon;
            return (
              <StaggerItem key={s.slug}>
                <article
                  data-testid={SERVICES.card(s.slug)}
                  className="group kn-card relative flex flex-col h-full"
                >
                  {/* Image top */}
                  <div className="kn-service-image aspect-[16/10] relative">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Metallic gold icon — top-left */}
                    <span
                      className="absolute top-4 left-4 w-14 h-14 rounded-full flex items-center justify-center z-20 transition-transform duration-500 group-hover:-rotate-12 group-hover:scale-110"
                      style={{
                        background: "linear-gradient(140deg, #8C6A1A 0%, #D4AF37 22%, #F8E9A1 45%, #FFFBE6 58%, #F0D87C 75%, #B8902F 100%)",
                        boxShadow:
                          "0 14px 30px -10px rgba(140,106,26,0.7), inset 0 1px 0 rgba(255,255,255,0.9), inset 0 -2px 4px rgba(140,106,26,0.35)",
                      }}
                    >
                      <Icon className="w-6 h-6 text-[#0B1437]" strokeWidth={1.9} />
                    </span>
                    {/* Descriptor tag — top-right */}
                    <span className="absolute top-5 right-4 z-10 text-[10px] uppercase tracking-[0.24em] font-semibold px-3 py-1.5 rounded-full bg-black/50 text-white/95 backdrop-blur-md border border-white/25 shadow-[0_10px_25px_-8px_rgba(0,0,0,0.6)]">
                      {s.tag}
                    </span>
                  </div>

                  <div className="p-8 lg:p-9 pt-8 flex flex-col justify-between flex-1">
                    <div>
                      <h3 className="font-display text-2xl lg:text-[1.65rem] font-medium tracking-tight text-[#0F172A]">
                        {s.title}
                      </h3>
                      <p className="mt-3 text-slate-600 font-body leading-relaxed text-[15px] max-w-md">
                        {s.blurb}
                      </p>
                    </div>
                    <div className="mt-8 flex items-center gap-2 text-sm font-medium">
                      <span className="kn-metallic-gold-text font-semibold">0{idx + 1}</span>
                      <span className="w-8 h-px bg-gradient-to-r from-[#8C6A1A] via-[#F8E9A1] to-[#8C6A1A]" />
                      <span className="text-slate-400 uppercase tracking-[0.18em] text-xs">
                        Knight Notary
                      </span>
                    </div>
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
};

/* -------------------- ABOUT -------------------- */
const About = () => {
  return (
    <section
      id="about"
      data-testid={ABOUT.root}
      className="py-24 lg:py-32 bg-[#0B1437] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <div className="relative" style={{ perspective: "1400px" }}>
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-[#D4AF37]/25 rounded-full blur-3xl" />
              <div
                className="relative rounded-3xl overflow-hidden transition-transform duration-500 hover:-translate-y-2 kn-about-3d"
                style={{
                  background: "linear-gradient(180deg,#EAF0FA 0%,#CBD6EA 100%)",
                  boxShadow:
                    "0 40px 90px -30px rgba(15,23,42,0.65), 0 22px 44px -20px rgba(30,42,94,0.55), inset 0 1px 0 rgba(255,255,255,0.9)",
                  transformStyle: "preserve-3d",
                }}
              >
                <img
                  src={SIGNING_URL}
                  alt="Damon Knight, commissioned notary public"
                  className="relative w-full h-auto max-h-[720px] object-contain block"
                />
              </div>

              {/* Bottom-right NNA Certified Signing Agent badge — half on photo, half on section background */}
              <div className="absolute right-6 lg:right-10 bottom-0 translate-y-1/2 z-20">
                <div className="relative w-28 h-28 lg:w-36 lg:h-36 rounded-full overflow-hidden ring-4 ring-white shadow-[0_30px_70px_-20px_rgba(15,23,42,0.55)] bg-white">
                  <img
                    src="https://customer-assets.emergentagent.com/job_knight-docs/artifacts/pbhpkhtj_recreate-in-4k_Own8-z-iXOaNnMg-6rr2ww_vVhfNGfCTK2xkmunwquRMg_cover_2k.png"
                    alt="Certified Notary Signing Agent — National Notary Association"
                    className="absolute inset-0 w-full h-full object-cover scale-[1.08]"
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#1E2A5E] text-white rounded-2xl p-6 max-w-[220px] shadow-2xl z-10">
                <Sparkles className="w-5 h-5 mb-3" style={{ stroke: "url(#kn-gold-grad)" }} />
                <p className="font-display text-xl leading-tight">
                  Trusted across Greater Cincinnati
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>About</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-white leading-tight">
              <span className="kn-metallic-gold-text">Built on precision,</span>
              <br />
              <span className="kn-metallic-gold-text">obsessed with trust.</span>
            </h2>
            <p className="mt-7 text-slate-300 leading-relaxed font-body text-[17px]">
              Knight Notary Services was founded on a simple idea: the moment a
              pen meets paper, something important is happening. Whether it's a
              mortgage, a power of attorney, or an apostille bound for another
              country — every document deserves a notary who shows up on time,
              dressed to represent, and prepared to the page.
            </p>
            <p className="mt-5 text-slate-300 leading-relaxed font-body text-[17px]">
              Damon Knight leads the practice as a commissioned notary public,
              certified loan signing agent, and your signature defender across
              the Greater Cincinnati area and beyond — <span className="font-semibold text-white">fully insured and bonded</span> for every appointment.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-5">
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/12 p-6 hover:border-[#D4AF37]/60 hover:shadow-[0_22px_44px_-22px_rgba(212,175,55,0.6)] transition-shadow duration-300 cursor-default"
              >
                <p className="font-display text-4xl kn-metallic-gold-text font-light">
                  100%
                </p>
                <p className="mt-2 text-sm text-slate-300 font-body">
                  Confidentiality guaranteed on every appointment
                </p>
              </motion.div>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/12 p-6 hover:border-[#D4AF37]/60 hover:shadow-[0_22px_44px_-22px_rgba(212,175,55,0.6)] transition-shadow duration-300 cursor-default"
              >
                <p className="font-display text-4xl font-light kn-metallic-gold-text">
                  24/7
                </p>
                <p className="mt-2 text-sm text-slate-300 font-body">
                  Around-the-clock availability for urgent signings
                </p>
                <p className="mt-2 text-[11.5px] text-slate-400 font-body italic">
                  *After 7 PM a convenience fee will be applied
                </p>
              </motion.div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/15 px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold kn-metallic-gold-text">
                <ShieldCheck className="w-3.5 h-3.5" style={{ stroke: "url(#kn-gold-grad)" }} />
                Insured &amp; Bonded
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" style={{ stroke: "url(#kn-gold-grad)" }} />
                <span className="kn-metallic-gold-text">NNA Certified</span>
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" style={{ stroke: "url(#kn-gold-grad)" }} />
                <span className="kn-metallic-gold-text">Background Screened</span>
              </span>
            </div>

            <a
              href={ZCAL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid={ABOUT.cta}
              className="mt-10 inline-flex items-center gap-2 rounded-full py-4 px-6 font-semibold text-[15px] text-[#0B1437] hover:-translate-y-0.5 hover:shadow-[0_22px_50px_-14px_rgba(212,175,55,0.75)] transition-all duration-300"
              style={{
                background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 45%,#B8902F 100%)",
                boxShadow: "0 14px 34px -12px rgba(212,175,55,0.65), inset 0 1px 0 rgba(255,255,255,0.7)",
              }}
            >
              Schedule a Signing
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- WHY KNIGHT NOTARY -------------------- */
const WHY_PILLARS = [
  "Extensive lending-industry knowledge",
  "Professional document presentation",
  "Mobile appointment convenience",
  "Respectful and confidential service",
  "Clear, timely communication",
  "Meticulous attention to signing instructions",
  "Ohio, Kentucky & Tri-State coverage",
  "NNA certified & background screened",
  "Fully insured & bonded",
  "Same-day and after-hours appointments",
];

const WhyKnight = () => {
  return (
    <section
      id="why"
      data-testid={WHY.root}
      className="py-24 lg:py-32 bg-[#0B1437]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <FlyIn from="left" className="lg:col-span-5">
            <span
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[10.5px] uppercase tracking-[0.24em] font-semibold mb-5 border border-[#D4AF37]/40"
              style={{
                background: "#050B26",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 8px 22px -12px rgba(0,0,0,0.6)",
              }}
            >
              <span className="kn-metallic-gold-text">Why Knight Notary Services</span>
            </span>
            <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-white leading-[1.02]">
              <span className="kn-metallic-gold-text">Professional service</span>
              <br />
              <span className="kn-metallic-gold-text">when accuracy matters most.</span>
            </h2>
            <p className="mt-6 text-slate-300 font-body leading-relaxed text-[17px]">
              Every appointment is treated as a professional engagement —
              organized, discreet, and focused on getting the documents
              completed correctly the first time. Every signing is <span className="font-semibold text-white">insured &amp; bonded</span>.
            </p>
          </FlyIn>

          <div className="lg:col-span-7">
            <StaggerGroup className="grid sm:grid-cols-2 gap-4">
              {WHY_PILLARS.map((p, i) => (
                <StaggerItem key={i}>
                  <div
                    data-testid={WHY.pill(i)}
                    className="bg-white/5 backdrop-blur-md border border-white/12 rounded-2xl p-5 flex items-start gap-4 hover:border-[#D4AF37]/60 hover:bg-white/10 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-20px_rgba(212,175,55,0.55)] transition-all duration-300"
                  >
                    <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)" }}>
                      <span className="font-display text-sm text-[#0B1437] font-semibold">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </span>
                    <p className="font-display text-[15.5px] text-white leading-snug pt-1">
                      {p}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- HOW IT WORKS (Animated Diagram) -------------------- */
const HOW_STEPS = [
  {
    icon: ClipboardCheck,
    title: "Request an Appointment",
    body: "Choose the service and share your preferred time and location.",
  },
  {
    icon: CalendarCheck,
    title: "Receive Confirmation",
    body: "We confirm details, ID requirements, and estimated fees.",
  },
  {
    icon: UserCheck,
    title: "Meet With the Notary",
    body: "The notary arrives prepared and verifies identity before signing.",
  },
  {
    icon: FileSignature,
    title: "Complete Your Documents",
    body: "Signing is completed carefully and documents are handled per instructions.",
  },
];

const HowItWorks = () => {
  return (
    <section
      id="how"
      data-testid={HOW.root}
      className="py-24 lg:py-32 bg-[#0B1437] relative overflow-hidden"
    >
      {/* Ambient gold glow */}
      <div className="kn-ambient-glow" style={{ background: "#D4AF37", width: 620, height: 620, top: "-14%", right: "-8%", opacity: 0.32 }} />
      <div className="kn-ambient-glow" style={{ background: "#1E2A5E", width: 720, height: 720, bottom: "-24%", left: "-10%", opacity: 0.55 }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <Reveal className="max-w-3xl mb-14">
          <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.6)" }}>How It Works</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-[1.02]">
            <span className="kn-metallic-gold-text">A simple, professional</span>
            <br />
            <span className="kn-metallic-gold-text">signing process.</span>
          </h2>
          <p className="mt-6 text-slate-300 font-body text-[16.5px] max-w-xl">
            From first call to final signature — a diagram of exactly what to expect.
          </p>
        </Reveal>

        {/* SVG animated diagram */}
        <div className="relative">
          {/* Curved connector — desktop */}
          <svg
            className="hidden lg:block absolute inset-x-0 top-[72px] w-full h-40 pointer-events-none"
            viewBox="0 0 1200 160"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="kn-flow-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8C6A1A" stopOpacity="0.2" />
                <stop offset="20%" stopColor="#D4AF37" stopOpacity="1" />
                <stop offset="50%" stopColor="#F8E9A1" stopOpacity="1" />
                <stop offset="80%" stopColor="#D4AF37" stopOpacity="1" />
                <stop offset="100%" stopColor="#8C6A1A" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            {/* Static base line */}
            <path
              d="M 60 80 Q 300 -20, 460 80 T 900 80 Q 1050 130, 1140 80"
              fill="none"
              stroke="rgba(255,255,255,0.14)"
              strokeWidth="1.5"
            />
            {/* Animated dashed line */}
            <path
              d="M 60 80 Q 300 -20, 460 80 T 900 80 Q 1050 130, 1140 80"
              fill="none"
              stroke="url(#kn-flow-grad)"
              strokeWidth="2"
              strokeLinecap="round"
              className="kn-flow-path"
            />
            {/* Moving pulse dot */}
            <circle r="6" fill="#F8E9A1">
              <animateMotion
                dur="7.5s"
                repeatCount="indefinite"
                path="M 60 80 Q 300 -20, 460 80 T 900 80 Q 1050 130, 1140 80"
              />
              <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite" />
            </circle>
          </svg>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 relative">
            {HOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  data-testid={HOW.step(i)}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: i * 0.15, ease: [0.2, 0.7, 0.15, 1] }}
                  className="relative text-center lg:text-left"
                >
                  <div className="mx-auto lg:mx-0 relative w-[136px] h-[136px] mb-7">
                    {/* Halo ring */}
                    <span className="absolute inset-[-14px] rounded-full kn-node-ring" style={{ background: "radial-gradient(circle at center, rgba(212,175,55,0.3) 0%, rgba(212,175,55,0) 65%)" }} />
                    {/* Metallic outer ring */}
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{
                        padding: "3px",
                        background: "linear-gradient(140deg, #8C6A1A 0%, #F8E9A1 30%, #FFFBE6 45%, #D4AF37 65%, #8C6A1A 100%)",
                        boxShadow: "0 20px 55px -18px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.6)",
                      }}
                    >
                      <span className="w-full h-full rounded-full bg-[#0B1437] flex items-center justify-center relative overflow-hidden">
                        <span className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.25), transparent 60%)" }} />
                        <Icon className="w-10 h-10 relative z-10" strokeWidth={1.5} style={{ stroke: "url(#kn-gold-grad)" }} />
                      </span>
                    </span>
                    {/* Number badge */}
                    <span
                      className="absolute -top-1 -right-1 z-20 w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold text-[#0B1437] shadow-[0_10px_20px_-6px_rgba(212,175,55,0.75)] font-display"
                      style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display text-xl lg:text-[1.4rem] font-medium tracking-tight text-white">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-slate-300 font-body leading-relaxed text-[14.5px] max-w-[280px] mx-auto lg:mx-0">
                    {s.body}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Bottom insured & bonded reassurance */}
          <div className="mt-16 flex flex-col sm:flex-row items-center gap-4 justify-center">
            <span className="kn-chip-insured">
              <ShieldCheck className="w-3.5 h-3.5" />
              Insured &amp; Bonded on every appointment
            </span>
            <a
              href={ZCAL_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-[#0B1437] hover:-translate-y-0.5 transition-transform"
              style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)", boxShadow: "0 18px 40px -14px rgba(212,175,55,0.55)" }}
            >
              Start the process
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- SERVICE AREAS -------------------- */
const AREAS_LIST = [
  {
    slug: "cincinnati",
    name: "Greater Cincinnati",
    region: "OHIO",
    body: "Primary service area — same-day availability",
    icon: HomeIcon,
  },
  {
    slug: "nky",
    name: "Northern Kentucky",
    region: "KENTUCKY",
    body: "Covington, Newport, Florence & surrounding",
    icon: MapPin,
  },
  {
    slug: "dayton",
    name: "Dayton",
    region: "OHIO",
    body: "Serving Montgomery & Greene counties",
    icon: Building2,
  },
  {
    slug: "columbus",
    name: "Columbus",
    region: "OHIO",
    body: "Central Ohio appointments by schedule",
    icon: Landmark,
  },
  {
    slug: "surrounding",
    name: "Surrounding Communities",
    region: "OH · KY",
    body: "Contact us to confirm coverage for your address",
    icon: Car,
  },
];

const ServiceAreas = () => {
  return (
    <section
      id="areas"
      data-testid={AREAS.root}
      className="py-24 lg:py-32 bg-[#0B1437] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>Service Areas</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-[1.02]">
            <span className="kn-metallic-gold-text">Serving the Tri-State</span>
            <br />
            <span className="kn-metallic-gold-text">region with precision.</span>
          </h2>
          <p className="mt-6 text-slate-300 font-body leading-relaxed text-[17px] max-w-2xl">
            Knight Notary Services travels throughout Ohio and Northern
            Kentucky — meeting clients at residences, corporate offices, title
            companies, hospitals, assisted-living communities, and mutually
            agreed public locations. Fully <span className="font-semibold text-white">insured &amp; bonded</span> across every county we serve.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Feature skyline card */}
          <div className="lg:col-span-6">
            <div className="relative rounded-3xl overflow-hidden h-full min-h-[540px] shadow-[0_30px_80px_-30px_rgba(15,23,42,0.55)] group">
              <img
                src={CINCY_AERIAL_URL}
                alt="Aerial daytime view of downtown Cincinnati along the Ohio River — with Great American Ball Park and Paycor Stadium visible on the riverfront"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1437] via-[#0B1437]/40 to-transparent" />
              <div className="absolute top-6 left-6">
                <span className="kn-chip-insured">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Insured &amp; Bonded
                </span>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10 text-white">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: "#D4AF37" }}>
                    <ShieldCheck className="w-3.5 h-3.5 text-[#0B1437]" />
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.24em] font-semibold kn-metallic-gold-text">
                    Headquartered in Cincinnati
                  </span>
                </div>
                <h3 className="font-display text-3xl lg:text-4xl font-light tracking-tight">
                  Greater Cincinnati Region
                </h3>
                <p className="mt-2 text-slate-200 font-body text-sm">
                  Ohio · Northern Kentucky · Surrounding Communities
                </p>

                <div className="mt-8 pt-6 border-t border-white/20 grid grid-cols-3 gap-6">
                  <div>
                    <p className="font-display text-3xl lg:text-4xl font-light kn-metallic-gold-text">4</p>
                    <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-300 mt-1">Metro Areas</p>
                  </div>
                  <div>
                    <p className="font-display text-3xl lg:text-4xl font-light kn-metallic-gold-text">
                      7 <span className="text-lg align-baseline">Days</span>
                    </p>
                    <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-300 mt-1">Availability</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl lg:text-3xl font-light kn-metallic-gold-text">On-Site</p>
                    <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-300 mt-1">Mobile Service</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Area rows */}
          <div className="lg:col-span-6 flex flex-col gap-3">
            {AREAS_LIST.map((a, i) => (
              <motion.a
                key={a.slug}
                href="#contact"
                data-testid={AREAS.card(a.slug)}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.2, 0.7, 0.15, 1] }}
                className="group flex items-center gap-5 bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-5 lg:p-6 hover:border-[#D4AF37]/70 hover:-translate-y-0.5 hover:bg-white/10 hover:shadow-[0_20px_44px_-18px_rgba(212,175,55,0.6)] transition-all duration-300"
              >
                <span
                  className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
                  }}
                >
                  <MapPin className="w-5 h-5 text-[#0B1437]" strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg lg:text-xl font-medium tracking-tight text-white">
                    {a.name}
                    <span className="ml-2 text-[11px] uppercase tracking-[0.22em] font-semibold kn-metallic-gold-text">
                      {a.region}
                    </span>
                  </p>
                  <p className="mt-0.5 text-slate-400 font-body text-[13.5px]">
                    {a.body}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </motion.a>
            ))}
            <a
              href={ZCAL_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 font-medium text-[#0B1437] transition-transform hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)", boxShadow: "0 18px 40px -14px rgba(212,175,55,0.65)" }}
            >
              Check Appointment Availability
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- FAQ -------------------- */
const FAQ_LIST = [
  {
    q: "Are you insured and bonded?",
    a: "Yes — Knight Notary Services is fully insured and bonded. Every appointment carries the same coverage, whether it's a single acknowledgment or a full loan-signing package, giving you and your title company complete peace of mind.",
  },
  {
    q: "What identification do I need for notarization?",
    a: "A current, government-issued photo ID such as a driver's license, state ID, or U.S. passport is required for each signer. The name on the ID must reasonably match the name on the document.",
  },
  {
    q: "Are same-day appointments available?",
    a: "Yes — same-day appointments are frequently available across Greater Cincinnati, Dayton, Northern Kentucky, and Columbus. After-hours availability is offered subject to a convenience fee. Call us at 1 (513) 296-6926 for the fastest response.",
  },
  {
    q: "Can you travel to a hospital or care facility?",
    a: "Absolutely. We regularly notarize documents at hospitals, hospice, rehabilitation, and assisted-living facilities. The signer must be alert, willing, and able to communicate — we'll confirm requirements before we arrive.",
  },
  {
    q: "What areas do you serve?",
    a: "Greater Cincinnati, Dayton, Northern Kentucky, and Columbus, plus surrounding communities across Ohio and Northern Kentucky. Reach out to confirm coverage for your specific address.",
  },
  {
    q: "Can a notary give legal advice?",
    a: "No. A notary public is not an attorney. We can complete notarial acts (acknowledgments, jurats, oaths, copy certifications) but cannot draft, interpret, or advise on the contents of your documents. Please consult a licensed attorney for legal questions.",
  },
  {
    q: "What is an apostille?",
    a: "An apostille is an authentication certificate that verifies the origin of a public document for use in another country that is party to the Hague Convention. We facilitate apostille requests through the Ohio Secretary of State on your behalf.",
  },
  {
    q: "How much does a notary appointment cost?",
    a: "Fees depend on document type, number of signers, travel distance, and time of day. Standard notarizations, loan signings, and apostille facilitation are priced transparently — request a quote and we'll confirm before scheduling.",
  },
  {
    q: "Do you accept credit cards?",
    a: "Yes — we accept all major credit/debit cards, ACH, cash, and business checks. Payment is due at the completion of the appointment unless prior arrangements are made with a title company or corporate client.",
  },
];

const FaqItem = ({ item, index, open, onToggle }) => {
  return (
    <div
      data-testid={FAQ.item(index)}
      className="border-b border-white/12"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-6 text-left py-6 group"
        aria-expanded={open}
      >
        <span className="font-display text-lg lg:text-xl font-medium text-white tracking-tight group-hover:kn-metallic-gold-text transition-colors">
          {item.q}
        </span>
        <span
          className={`shrink-0 mt-1 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 ${
            open
              ? "text-[#0B1437] rotate-180 border-transparent"
              : "text-white border-white/25"
          }`}
          style={open ? { background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)" } : {}}
        >
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-500 ${
          open ? "max-h-[400px] pb-7 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p className="text-slate-300 font-body leading-relaxed text-[15.5px] pr-14">
          <span className="kn-metallic-gold-text">{item.a}</span>
        </p>
      </div>
    </div>
  );
};

const Faqs = () => {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid={FAQ.root}
      className="py-24 lg:py-32 bg-[#0B1437] relative overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="mb-12 max-w-3xl">
          <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>FAQ</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-[1.02]">
            <span className="kn-metallic-gold-text">Frequently asked</span>
            <br />
            <span className="kn-metallic-gold-text">questions.</span>
          </h2>
        </div>

        <div className="border-t border-white/12">
          {FAQ_LIST.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              index={i}
              open={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </div>

        <p className="mt-10 text-xs text-slate-400 font-body italic">
          Knight Notary Services is not a law firm and does not provide legal
          advice. Please consult a licensed attorney for legal guidance.
        </p>
      </div>
    </section>
  );
};

/* -------------------- TESTIMONIALS -------------------- */
const TESTIMONIALS_LIST = [
  {
    quote:
      "Damon drove out to our title office at 6:30 in the evening and handled a 70-page loan signing without a single hiccup. The borrower thought he was a lawyer. That's the bar.",
    name: "Marisol Adkins",
    role: "Senior Closer, Queen City Title",
    rating: 5,
  },
  {
    quote:
      "I needed an apostille for documents headed to Germany and had no idea where to start. Knight Notary walked me through every step and turned it around in two days.",
    name: "Daniel Okafor",
    role: "Small Business Owner, Cincinnati",
    rating: 5,
  },
  {
    quote:
      "My grandmother is in hospice and we needed a POA notarized at the hospital on a Sunday evening. Damon showed up in a suit, was incredibly kind, and didn't rush her once.",
    name: "Erica Pham",
    role: "Family Caregiver, Mason OH",
    rating: 5,
  },
  {
    quote:
      "We've used three notaries for our real estate practice this year. Knight is the only one we're still using. Punctual, professional, and our clients always comment on it.",
    name: "Tony Ricci",
    role: "Realtor, RE/MAX Greater Cincinnati",
    rating: 5,
  },
  {
    quote:
      "Refinance documents arrived at 4pm and the lender needed them notarized same day. Damon was at my kitchen table by 6. Saved my rate lock.",
    name: "Aaliyah Brooks",
    role: "Homeowner, Northern Kentucky",
    rating: 5,
  },
];

const Testimonials = () => {
  return (
    <section
      id="testimonials"
      data-testid={TESTIMONIALS.root}
      className="py-24 lg:py-32 bg-[#0B1437] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-6">
            <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>Testimonials</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white leading-[1.02]">
              <span className="kn-metallic-gold-text">Trusted by the</span>
              <br />
              <span className="kn-metallic-gold-text">Greater Cincinnati area and beyond.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            <p className="text-slate-300 font-body leading-relaxed text-[17px] max-w-xl">
              Real stories from clients who needed something important
              notarized — and got the kind of service that's hard to find
              anywhere else.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex -space-x-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5"
                    fill="url(#kn-gold-grad)"
                    style={{ stroke: "url(#kn-gold-grad)" }}
                  />
                ))}
              </div>
              <span className="font-display text-lg text-white">5.0</span>
              <span className="kn-label" style={{ color: "rgba(255,255,255,0.55)" }}>From every client we serve</span>
            </div>
          </div>
        </div>

        <div className="overflow-hidden">
          <div className="kn-marquee-slow flex gap-5 w-max">
            {[...TESTIMONIALS_LIST, ...TESTIMONIALS_LIST].map((t, i) => {
              return (
                <article
                  key={i}
                  data-testid={TESTIMONIALS.card(i % TESTIMONIALS_LIST.length)}
                  className="kn-card kn-card-3d p-8 lg:p-9 flex flex-col justify-between w-[360px] lg:w-[420px] shrink-0 min-h-[300px] bg-white"
                >
                  <div>
                    <Quote
                      className="w-7 h-7 mb-5"
                      strokeWidth={1.5}
                      style={{ stroke: "url(#kn-gold-grad)" }}
                    />
                    <p className="font-display text-[17px] lg:text-[18px] font-light leading-[1.5] tracking-tight text-[#0F172A]">
                      "{t.quote}"
                    </p>
                  </div>
                  <footer className="mt-7 flex items-center justify-between gap-4">
                    <div>
                      <p className="font-display font-medium tracking-tight text-[#0F172A]">
                        {t.name}
                      </p>
                      <p className="text-[12.5px] font-body mt-0.5 text-slate-500">
                        {t.role}
                      </p>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[...Array(t.rating)].map((_, j) => (
                        <Star
                          key={j}
                          className="w-3.5 h-3.5"
                          fill="url(#kn-gold-grad)"
                          style={{ stroke: "url(#kn-gold-grad)" }}
                        />
                      ))}
                    </div>
                  </footer>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- CONTACT -------------------- */
const Contact = () => {
  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="py-24 lg:py-32 bg-[#0B1437] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left info card */}
          <div className="lg:col-span-5">
            <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.55)" }}>Contact</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-white leading-tight">
              Let's get it
              <br />
              <span className="kn-metallic-gold-text">notarized.</span>
            </h2>
            <p className="mt-6 text-slate-300 font-body leading-relaxed">
              Reach out for same-day or scheduled appointments. Mobile notary
              available across Greater Cincinnati — every appointment <span className="font-semibold text-white">insured &amp; bonded</span>.
            </p>

            <div className="mt-10 space-y-2">
              <a
                href="tel:+15132966926"
                data-testid={CONTACT.phoneLink}
                className="group flex items-center gap-4 p-5 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md hover:border-[#D4AF37]/60 hover:bg-white/10 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(212,175,55,0.55)] transition-all duration-300"
              >
                <span className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)" }}>
                  <Phone className="w-4.5 h-4.5 text-[#0B1437]" />
                </span>
                <span className="flex-1">
                  <span className="kn-label block" style={{ color: "rgba(255,255,255,0.55)" }}>Call</span>
                  <span className="font-display text-lg text-white block">
                    1 (513) 296-6926
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </a>

              <a
                href="tel:+15133994989"
                data-testid="contact-fax-link"
                className="group flex items-center gap-4 p-5 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md hover:border-[#D4AF37]/60 hover:bg-white/10 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(212,175,55,0.55)] transition-all duration-300"
              >
                <span className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)" }}>
                  <Printer className="w-4.5 h-4.5 text-[#0B1437]" />
                </span>
                <span className="flex-1">
                  <span className="kn-label block" style={{ color: "rgba(255,255,255,0.55)" }}>Fax</span>
                  <span className="font-display text-lg text-white block">
                    1 (513) 399-4989
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </a>

              <a
                href="mailto:damon.knight@knight-notary.com"
                data-testid={CONTACT.emailLink}
                className="group flex items-center gap-4 p-5 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md hover:border-[#D4AF37]/60 hover:bg-white/10 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(212,175,55,0.55)] transition-all duration-300"
              >
                <span className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 55%,#B8902F 100%)" }}>
                  <Mail className="w-4.5 h-4.5 text-[#0B1437]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="kn-label block" style={{ color: "rgba(255,255,255,0.55)" }}>Email</span>
                  <span className="font-display text-base text-white block truncate">
                    damon.knight@knight-notary.com
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white group-hover:translate-x-1 transition-all" />
              </a>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="p-5 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md hover:border-[#D4AF37]/60 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(212,175,55,0.55)] transition-all duration-300 cursor-default">
                  <MapPin className="w-4 h-4 mb-2" style={{ stroke: "url(#kn-gold-grad)" }} />
                  <p className="kn-label" style={{ color: "rgba(255,255,255,0.55)" }}>Service area</p>
                  <p className="font-display text-base text-white mt-1 leading-tight">
                    Cincinnati, Dayton, Northern KY, and Columbus
                  </p>
                </div>
                <div className="p-5 rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md hover:border-[#D4AF37]/60 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(212,175,55,0.55)] transition-all duration-300 cursor-default">
                  <Clock className="w-4 h-4 mb-2" style={{ stroke: "url(#kn-gold-grad)" }} />
                  <p className="kn-label" style={{ color: "rgba(255,255,255,0.55)" }}>Hours</p>
                  <p className="font-display text-base text-white mt-1 leading-tight">
                    After 7 PM available
                  </p>
                  <p className="text-[12px] text-slate-400 mt-1 font-body leading-snug">
                    After 7 PM, a surcharge fee applies
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/15 px-3.5 py-1.5 text-[10.5px] uppercase tracking-[0.22em] font-semibold kn-metallic-gold-text">
                  <ShieldCheck className="w-3.5 h-3.5" style={{ stroke: "url(#kn-gold-grad)" }} />
                  Insured &amp; Bonded
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3.5 py-1.5 text-[10.5px] uppercase tracking-[0.22em] font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5" style={{ stroke: "url(#kn-gold-grad)" }} />
                  <span className="kn-metallic-gold-text">NNA Certified</span>
                </span>
              </div>

              <a
                href={ZCAL_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-schedule-cta"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full py-4 px-6 font-semibold text-[15px] text-[#0B1437] hover:-translate-y-0.5 hover:shadow-[0_22px_50px_-14px_rgba(212,175,55,0.75)] transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg,#F8E9A1 0%,#D4AF37 45%,#B8902F 100%)",
                  boxShadow: "0 14px 34px -12px rgba(212,175,55,0.65), inset 0 1px 0 rgba(255,255,255,0.7)",
                }}
              >
                Schedule a Signing
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right form (external Ninjapipe embed) */}
          <div className="lg:col-span-7 lg:self-start">
            <div
              data-testid={CONTACT.form}
              className="bg-white border border-white/10 rounded-3xl p-4 lg:p-6 shadow-[0_36px_90px_-30px_rgba(0,0,0,0.55)] hover:shadow-[0_46px_110px_-30px_rgba(212,175,55,0.35)] hover:border-[#D4AF37]/40 transition-all duration-500 overflow-hidden"
            >
              <iframe
                src="https://knight-notary-services.ninjapipe.app/forms/kns-website-form"
                width="680"
                height="1295"
                frameBorder="0"
                title="KNS Website Form"
                className="block mx-auto max-w-full rounded-lg border-0"
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)", width: "680px", height: "1295px", maxWidth: "100%" }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------- FOOTER -------------------- */
const Footer = () => {
  return (
    <footer
      data-testid={FOOTER.root}
      className="bg-[#0B1437] text-white pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div>
              <p
                className="text-xl tracking-tight font-bold"
                style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
              >
                Knight Notary Services
              </p>
              <p className="text-sm font-body mt-1 kn-metallic-gold-text font-semibold">
                Your Signature Defender
              </p>
            </div>
            <p className="mt-6 text-slate-400 max-w-md font-body leading-relaxed text-sm">
              Premium, mobile notary services serving Greater Cincinnati,
              Dayton, Northern KY, and Columbus — commissioned, <span className="text-white font-medium">insured &amp; bonded</span>,
              and ready when you are.
            </p>

            <p className="kn-label mt-8 mb-3 kn-metallic-gold-text">Follow</p>
            <div className="flex items-center gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.name}
                  data-testid={`footer-${s.testid}`}
                  className="kn-header-social group w-10 h-10 flex items-center justify-center rounded-full bg-white/10 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/15 hover:border-[#F8E9A1] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_4px_18px_rgba(212,175,55,0.25)]"
                >
                  <SocialIcon name={s.name} className="w-[18px] h-[18px]" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <p className="kn-label mb-4 kn-metallic-gold-text">Services</p>
            <ul className="grid grid-cols-2 gap-y-3 font-body text-sm text-slate-300">
              {SERVICES_LIST.map((s) => (
                <li key={s.slug}>
                  <a href="#services" className="hover:text-white">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3 lg:text-right">
            <p className="kn-label mb-4 kn-metallic-gold-text">Contact</p>
            <ul className="space-y-3 font-body text-sm text-slate-300">
              <li>
                <a
                  href="tel:+15132966926"
                  className="flex items-center gap-2 hover:text-white transition-colors lg:justify-end"
                >
                  <Phone className="w-3.5 h-3.5" style={{ stroke: "url(#kn-gold-grad)" }} />
                  1 (513) 296-6926
                </a>
              </li>
              <li>
                <span className="flex items-center gap-2 text-slate-300 lg:justify-end">
                  <Printer className="w-3.5 h-3.5" style={{ stroke: "url(#kn-gold-grad)" }} />
                  Fax 1 (513) 399-4989
                </span>
              </li>
              <li>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold kn-metallic-gold-text lg:ml-auto mt-2">
                  <ShieldCheck className="w-3 h-3" style={{ stroke: "url(#kn-gold-grad)" }} />
                  Insured &amp; Bonded
                </span>
              </li>
            </ul>

            <div className="mt-6 flex lg:justify-end">
              <div className="relative w-28 h-28 rounded-full overflow-hidden ring-4 ring-white shadow-[0_24px_60px_-15px_rgba(255,255,255,0.35)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_knight-docs/artifacts/pbhpkhtj_recreate-in-4k_Own8-z-iXOaNnMg-6rr2ww_vVhfNGfCTK2xkmunwquRMg_cover_2k.png"
                  alt="Certified Notary Signing Agent — National Notary Association"
                  className="absolute inset-0 w-full h-full object-cover scale-[1.08]"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-slate-500 font-body">
          <p>© {new Date().getFullYear()} Knight Notary Services. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a
              href="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
            <span className="w-px h-3 bg-white/20" aria-hidden />
            <a
              href="/terms"
              data-testid="footer-terms-link"
              className="hover:text-white transition-colors"
            >
              Terms and Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* -------------------- MANIFESTO (Numbered chapters) -------------------- */
const MANIFESTO = [
  {
    n: "01",
    title: "Show up prepared.",
    body: "We arrive early, dressed to represent, with the exact tools and forms your document requires. There is no surprise. There is only readiness.",
  },
  {
    n: "02",
    title: "Handle every page.",
    body: "Loan packages can run 70+ pages. Each one is read, initialed, and dated with the borrower — nothing is skipped, nothing is rushed.",
  },
  {
    n: "03",
    title: "Protect the signer.",
    body: "ID verification, willingness, awareness, and confidentiality are non-negotiable. Insured &amp; bonded — because trust is a legal instrument.",
  },
  {
    n: "04",
    title: "Deliver it clean.",
    body: "Documents are returned per instructions the same day when possible. Title companies, lenders, and families all get the same care.",
  },
];

const Manifesto = () => {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-root"
      className="relative py-28 lg:py-40 bg-[#0B1437] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <Reveal className="max-w-3xl mb-16 lg:mb-20 text-center mx-auto">
          <p className="kn-label mb-5" style={{ color: "rgba(255,255,255,0.6)" }}>The Manifesto</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[5.5rem] font-light tracking-[-0.035em] text-white leading-[0.95]">
            <span className="kn-metallic-gold-text">Four principles.</span>
            <br />
            <span className="kn-metallic-gold-text italic font-extralight">Zero exceptions.</span>
          </h2>
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="w-14 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <span className="text-[11px] uppercase tracking-[0.32em] font-semibold kn-metallic-gold-text">Signed by Damon Knight</span>
            <span className="w-14 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          </div>
        </Reveal>

        {/* Animated orbital diagram */}
        <div className="relative mx-auto w-full max-w-[900px] aspect-square lg:aspect-[16/12]">
          <svg
            viewBox="0 0 900 700"
            className="absolute inset-0 w-full h-full"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <radialGradient id="kn-manifesto-center" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#F8E9A1" stopOpacity="0.95" />
                <stop offset="55%" stopColor="#D4AF37" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#8C6A1A" stopOpacity="0.6" />
              </radialGradient>
              <linearGradient id="kn-manifesto-spoke" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity="0" />
                <stop offset="50%" stopColor="#F8E9A1" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Concentric orbit rings */}
            <circle cx="450" cy="350" r="230" fill="none" stroke="rgba(212,175,55,0.15)" strokeWidth="1" strokeDasharray="4 8" />
            <circle cx="450" cy="350" r="150" fill="none" stroke="rgba(212,175,55,0.22)" strokeWidth="1" strokeDasharray="3 6" />

            {/* Spokes to the four cardinal nodes */}
            {[
              { x2: 450, y2: 90 },   // top
              { x2: 750, y2: 350 },  // right
              { x2: 450, y2: 610 },  // bottom
              { x2: 150, y2: 350 },  // left
            ].map((p, i) => (
              <line
                key={i}
                x1="450"
                y1="350"
                x2={p.x2}
                y2={p.y2}
                stroke="url(#kn-manifesto-spoke)"
                strokeWidth="1.5"
                className="kn-flow-path"
              />
            ))}

            {/* Traveling pulse dots along each spoke */}
            {[0, 1, 2, 3].map((i) => (
              <circle key={i} r="5" fill="#F8E9A1">
                <animateMotion
                  dur={`${3 + i * 0.4}s`}
                  repeatCount="indefinite"
                  begin={`${i * 0.4}s`}
                  path={
                    i === 0
                      ? "M 450 350 L 450 90"
                      : i === 1
                      ? "M 450 350 L 750 350"
                      : i === 2
                      ? "M 450 350 L 450 610"
                      : "M 450 350 L 150 350"
                  }
                />
                <animate attributeName="opacity" values="0;1;0" dur={`${3 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.4}s`} />
              </circle>
            ))}

            {/* Center gold hub */}
            <circle cx="450" cy="350" r="46" fill="url(#kn-manifesto-center)" />
            <circle cx="450" cy="350" r="30" fill="#0B1437" stroke="rgba(255,255,255,0.35)" strokeWidth="0.5" />
            <text x="450" y="356" textAnchor="middle" fontFamily="system-ui" fontSize="14" fontWeight="600" fill="#F8E9A1" letterSpacing="0.15em">
              KNS
            </text>
          </svg>

          {/* HTML overlay: 4 principle cards positioned at cardinal points */}
          {MANIFESTO.map((c, i) => {
            const pos = [
              { top: "0%",   left: "50%", translate: "-translate-x-1/2 -translate-y-2" },
              { top: "50%",  left: "100%", translate: "-translate-x-full -translate-y-1/2 pl-6" },
              { top: "100%", left: "50%", translate: "-translate-x-1/2 -translate-y-full" },
              { top: "50%",  left: "0%",  translate: "translate-y-[-50%] pr-6" },
            ][i];
            return (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, scale: 0.85, y: 12 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: 0.2 + i * 0.15, ease: [0.2, 0.7, 0.15, 1] }}
                className={`absolute w-[240px] lg:w-[260px] ${pos.translate}`}
                style={{ top: pos.top, left: pos.left }}
              >
                <div className="relative rounded-2xl border border-[#D4AF37]/40 bg-white/[0.04] backdrop-blur-md p-5 hover:border-[#F8E9A1] hover:bg-white/[0.08] transition-all duration-500 shadow-[0_20px_60px_-25px_rgba(212,175,55,0.45)]">
                  <span
                    className="font-display text-4xl font-extralight leading-none kn-metallic-gold-text block mb-2"
                    style={{ letterSpacing: "-0.05em" }}
                  >
                    {c.n}
                  </span>
                  <h3 className="font-display text-lg lg:text-xl font-medium text-white tracking-tight leading-tight">
                    {c.title}
                  </h3>
                  <p
                    className="mt-2 text-slate-300 font-body text-[12.5px] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: c.body }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};


/* -------------------- PAGE -------------------- */
const Home = () => {
  useLenis();
  return (
    <div className="bg-white">
      <GoldIconDefs />
      <Navbar />
      <Hero />
      <TrustStrip />
      <Services />
      <WhyKnight />
      <HowItWorks />
      <About />
      <ServiceAreas />
      <Testimonials />
      <Faqs />
      <Contact />
      <Footer />
      <AfterHoursHotline />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            fontFamily: "Manrope, sans-serif",
            borderRadius: "12px",
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
